import React, { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Layout from '../../components/ui/Layout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';


import ApprovalPanel from './components/ApprovalPanel';
import FilterBar from './components/FilterBar';
import WorkflowStats from './components/WorkflowStats';
import SubHeader from '../../components/ui/SubHeader';
import ActionBoxes from '../../components/ui/ActionBoxes';
import MarketingContractModal from './components/MarketingContractModal';
import MarketingContractDetails from './components/MarketingContractDetails';
import BuyerRepresentationContractModal from './components/BuyerRepresentationContractModal';
import ContractTypeSelectionModal from './components/ContractTypeSelectionModal';
import ContractSourceSelectionModal from './components/ContractSourceSelectionModal';
import ContractArchiveModal from './components/ContractArchiveModal';
import RecommendationCard from './components/RecommendationCard';
import RecommendationDetails from './components/RecommendationDetails';
import RecommendationApprovalPanel from './components/RecommendationApprovalPanel';
import { generateClientRecommendations, saveRecommendation, getRecommendations, approveRecommendation, rejectRecommendation, sendRecommendationToClient } from '../../services/aiRecommendationService';
import { clientService } from '../../services/clientService';
import { propertyService } from '../../services/propertyService';
import { contractService } from '../../services/contractService';
import { useAuth } from '../../contexts/AuthContext';
import WorkflowAutomationModal from './components/WorkflowAutomationModal';
import WorkflowJobsModal from './components/WorkflowJobsModal';
import WorkflowMonitoringDashboard from './components/WorkflowMonitoringDashboard';
import AutomationRulesModal from './components/AutomationRulesModal';
import { automationRulesService } from '../../services/automationRulesService';
import realtimeNotificationService from '../../services/realtimeNotificationService';
import ContractCard from './components/ContractCard';




const ContractApprovalWorkflow = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [userRole] = useState('contract_specialist');
  const [userName] = useState('أحمد محمد');
  const [selectedContractId, setSelectedContractId] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [showNewContractModal, setShowNewContractModal] = useState(false);
  const [showMarketingContractModal, setShowMarketingContractModal] = useState(false);
  const [showBuyerRepresentationModal, setShowBuyerRepresentationModal] = useState(false);
  const [selectedContractForMarketing, setSelectedContractForMarketing] = useState(null);
  const [selectedContractForBuyer, setSelectedContractForBuyer] = useState(null);
  const [showContractTypeModal, setShowContractTypeModal] = useState(false);
  const [selectedContractTypeConfig, setSelectedContractTypeConfig] = useState(null);
  const [showContractSourceModal, setShowContractSourceModal] = useState(false);
  const [showContractArchiveModal, setShowContractArchiveModal] = useState(false);
  const [activeTab, setActiveTab] = useState('contracts');
  const [recommendations, setRecommendations] = useState([]);
  const [selectedRecommendationId, setSelectedRecommendationId] = useState(null);
  const [isGeneratingRecommendation, setIsGeneratingRecommendation] = useState(false);
  const [clients, setClients] = useState([]);
  const [properties, setProperties] = useState([]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showApprovedModal, setShowApprovedModal] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    priority: 'all',
    stage: 'all'
  });
  const [showWorkflowAutomationModal, setShowWorkflowAutomationModal] = useState(false);
  const [showWorkflowJobsModal, setShowWorkflowJobsModal] = useState(false);
  const [showMonitoringDashboard, setShowMonitoringDashboard] = useState(false);
  const [showAutomationRulesModal, setShowAutomationRulesModal] = useState(false);
  const [showDocumentManager, setShowDocumentManager] = useState(false);

  // New states for error handling and visual feedback
  const [isLoadingContractAction, setIsLoadingContractAction] = useState(false);
  const [contractActionError, setContractActionError] = useState(null);
  const [lastAttemptedAction, setLastAttemptedAction] = useState(null);

  // Real-time notification states
  const [realtimeNotification, setRealtimeNotification] = useState(null);
  const [showRealtimeToast, setShowRealtimeToast] = useState(false);

  // NEW: States for real data
  const [contracts, setContracts] = useState([]);
  const [isLoadingContracts, setIsLoadingContracts] = useState(true);
  const [contractsError, setContractsError] = useState(null);

  // Handle URL parameters to open specific modals
  useEffect(() => {
    const params = new URLSearchParams(location?.search);
    const action = params?.get('action');
    
    if (action === 'automation') {
      setShowWorkflowAutomationModal(true);
    } else if (action === 'monitoring') {
      setShowMonitoringDashboard(true);
    } else if (action === 'jobs') {
      setShowWorkflowJobsModal(true);
    } else if (action === 'rules') {
      setShowAutomationRulesModal(true);
    }
  }, [location?.search]);

  // Setup real-time subscriptions for contracts and recommendations
  useEffect(() => {
    // Subscribe to contract changes
    const unsubscribeContracts = realtimeNotificationService?.subscribeToContracts((notification) => {
      console.log('Real-time contract update:', notification);
      
      // Show toast notification
      const message = notification?.event === 'INSERT' ?'تم إضافة عقد جديد'
        : notification?.event === 'UPDATE' ?'تم تحديث عقد' :'تم حذف عقد';
      
      setRealtimeNotification({
        message,
        type: 'contract',
        data: notification?.data
      });
      setShowRealtimeToast(true);
      
      // Auto-hide toast after 5 seconds
      setTimeout(() => setShowRealtimeToast(false), 5000);
    });

    // Subscribe to marketing contracts
    const unsubscribeMarketing = realtimeNotificationService?.subscribeToMarketingContracts((notification) => {
      console.log('Real-time marketing contract update:', notification);
      
      const message = notification?.event === 'INSERT' ?'تم إضافة عقد تسويق جديد'
        : notification?.event === 'UPDATE' ?'تم تحديث عقد تسويق' :'تم حذف عقد تسويق';
      
      setRealtimeNotification({
        message,
        type: 'marketing_contract',
        data: notification?.data
      });
      setShowRealtimeToast(true);
      setTimeout(() => setShowRealtimeToast(false), 5000);
    });

    // Subscribe to buyer representation contracts
    const unsubscribeBuyer = realtimeNotificationService?.subscribeToBuyerContracts((notification) => {
      console.log('Real-time buyer contract update:', notification);
      
      const message = notification?.event === 'INSERT' ?'تم إضافة عقد تمثيل مشتري جديد'
        : notification?.event === 'UPDATE' ?'تم تحديث عقد تمثيل مشتري' :'تم حذف عقد تمثيل مشتري';
      
      setRealtimeNotification({
        message,
        type: 'buyer_contract',
        data: notification?.data
      });
      setShowRealtimeToast(true);
      setTimeout(() => setShowRealtimeToast(false), 5000);
    });

    // Subscribe to recommendations
    const unsubscribeRecommendations = realtimeNotificationService?.subscribeToRecommendations((notification) => {
      console.log('Real-time recommendation update:', notification);
      
      const message = notification?.event === 'INSERT' ?'تم إضافة توصية عميل جديدة'
        : notification?.event === 'UPDATE' ?'تم تحديث توصية عميل' :'تم حذف توصية عميل';
      
      setRealtimeNotification({
        message,
        type: 'recommendation',
        data: notification?.data
      });
      setShowRealtimeToast(true);
      setTimeout(() => setShowRealtimeToast(false), 5000);
    });

    // Subscribe to contract approvals
    const unsubscribeApprovals = realtimeNotificationService?.subscribeToContractApprovals((notification) => {
      console.log('Real-time approval update:', notification);
      
      const message = notification?.event === 'INSERT' ?'تم إضافة موافقة جديدة على عقد'
        : notification?.event === 'UPDATE' ?'تم تحديث موافقة عقد' :'تم حذف موافقة عقد';
      
      setRealtimeNotification({
        message,
        type: 'approval',
        data: notification?.data
      });
      setShowRealtimeToast(true);
      setTimeout(() => setShowRealtimeToast(false), 5000);
    });

    // Subscribe to workflow jobs
    const unsubscribeJobs = realtimeNotificationService?.subscribeToWorkflowJobs((notification) => {
      console.log('Real-time workflow job update:', notification);
      
      const message = notification?.event === 'INSERT' ?'تم إنشاء مهمة سير عمل جديدة'
        : notification?.event === 'UPDATE' ?'تم تحديث مهمة سير عمل' :'تم حذف مهمة سير عمل';
      
      setRealtimeNotification({
        message,
        type: 'workflow_job',
        data: notification?.data
      });
      setShowRealtimeToast(true);
      setTimeout(() => setShowRealtimeToast(false), 5000);
    });

    // Cleanup subscriptions on unmount
    return () => {
      unsubscribeContracts();
      unsubscribeMarketing();
      unsubscribeBuyer();
      unsubscribeRecommendations();
      unsubscribeApprovals();
      unsubscribeJobs();
    };
  }, []);

  const handleQuickMenuToggle = () => {
    console.log('Quick menu toggled');
  };

  // NEW: Load contracts from Supabase
  useEffect(() => {
    if (user?.id && activeTab === 'contracts') {
      loadContracts();
    }
  }, [user?.id, activeTab, filters]);

  const loadContracts = async () => {
    try {
      setIsLoadingContracts(true);
      setContractsError(null);
      
      const data = await contractService?.filterContracts(user?.id, filters);
      setContracts(data);
    } catch (error) {
      console.error('Error loading contracts:', error);
      setContractsError(error?.message || 'فشل في تحميل العقود');
    } finally {
      setIsLoadingContracts(false);
    }
  };

  const selectedContract = useMemo(() => {
    return contracts?.find((c) => c?.id === selectedContractId);
  }, [contracts, selectedContractId]);

  const stats = useMemo(() => {
    const pendingReview = contracts?.filter((c) => c?.status === 'pending')?.length || 0;
    const requiresYourApproval = contracts?.filter((c) => {
      if (userRole === 'admin') return c?.status === 'pending' || c?.status === 'approved';
      if (userRole === 'contract_specialist') return c?.status === 'pending';
      return false;
    })?.length || 0;
    const approved = contracts?.filter((c) => c?.status === 'approved')?.length || 0;
    const rejected = contracts?.filter((c) => c?.status === 'rejected')?.length || 0;

    return { pendingReview, requiresYourApproval, approved, rejected };
  }, [contracts, userRole]);

  const filteredContracts = useMemo(() => {
    return contracts?.filter((contract) => {
      const matchesSearch = filters?.search === '' ||
      contract?.contract_number?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
      contract?.client?.name?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
      contract?.property?.title?.toLowerCase()?.includes(filters?.search?.toLowerCase());

      const matchesStatus = filters?.status === 'all' || contract?.status === filters?.status;

      return matchesSearch && matchesStatus;
    });
  }, [contracts, filters]);

  const filteredRecommendations = useMemo(() => {
    return recommendations?.filter((rec) => {
      if (filters?.status !== 'all' && rec?.approvalStatus !== filters?.status) return false;
      if (filters?.priority !== 'all' && rec?.priority !== filters?.priority) return false;
      if (filters?.search && !rec?.clientName?.toLowerCase()?.includes(filters?.search?.toLowerCase())) return false;
      return true;
    });
  }, [recommendations, filters]);

  const selectedRecommendation = useMemo(() => {
    return recommendations?.find((r) => r?.id === selectedRecommendationId);
  }, [recommendations, selectedRecommendationId]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApprove = async (contractId) => {
    try {
      console.log('Approving contract:', contractId);
      await contractService?.updateContractStatus(contractId, 'approved');
      
      // Add approval record
      await contractService?.addContractApproval({
        contractId,
        approverName: userName,
        approverRole: userRole,
        action: 'approved',
        comments: 'تمت الموافقة'
      }, user?.id);
      
      // Reload contracts
      await loadContracts();
      alert('تمت الموافقة على العقد بنجاح');
    } catch (error) {
      console.error('Error approving contract:', error);
      alert('فشل في الموافقة على العقد: ' + error?.message);
    }
  };

  const handleReject = async (contractId, reason) => {
    try {
      console.log('Rejecting contract:', contractId, 'Reason:', reason);
      await contractService?.updateContractStatus(contractId, 'rejected');
      
      // Add rejection record
      await contractService?.addContractApproval({
        contractId,
        approverName: userName,
        approverRole: userRole,
        action: 'rejected',
        comments: reason || 'تم الرفض'
      }, user?.id);
      
      // Reload contracts
      await loadContracts();
      alert('تم رفض العقد');
    } catch (error) {
      console.error('Error rejecting contract:', error);
      alert('فشل في رفض العقد: ' + error?.message);
    }
  };

  const handleRequestChanges = async (contractId, changes) => {
    try {
      console.log('Requesting changes for contract:', contractId, 'Changes:', changes);
      
      // Add request changes record
      await contractService?.addContractApproval({
        contractId,
        approverName: userName,
        approverRole: userRole,
        action: 'requested_changes',
        comments: changes
      }, user?.id);
      
      // Reload contracts
      await loadContracts();
      alert('تم طلب التعديلات');
    } catch (error) {
      console.error('Error requesting changes:', error);
      alert('فشل في طلب التعديلات: ' + error?.message);
    }
  };

  const handleEscalate = (contractId) => {
    console.log('Escalating contract:', contractId);
    alert('تم تصعيد العقد للمشرف');
  };

  const handleExportContract = (contractId) => {
    console.log('Exporting contract:', contractId);
    alert('جاري تصدير العقد...');
  };

  const handlePrintContract = (contractId) => {
    console.log('Printing contract:', contractId);
    window.print();
  };

  const handleDownload = (contractId) => {
    console.log('Downloading contract:', contractId);
    alert(`جاري تحميل العقد ${contractId} بصيغة PDF`);
  };

  // Enhanced Make Contract button handler
  const handleMakeContractClick = () => {
    console.log('[INFO] Make Contract button clicked at:', new Date()?.toISOString());
    setIsLoadingContractAction(true);
    setContractActionError(null);
    setLastAttemptedAction({ action: 'openContractTypeModal', data: null });

    try {
      console.log('[DEBUG] Opening contract type selection modal');
      setShowContractTypeModal(true);
      setIsLoadingContractAction(false);
      console.log('[SUCCESS] Contract type modal opened successfully');
    } catch (error) {
      console.error('[ERROR] Failed to open contract type modal:', {
        error: error?.message,
        stack: error?.stack,
        timestamp: new Date()?.toISOString()
      });
      setContractActionError({
        message: 'فشل في فتح نافذة اختيار نوع العقد. يرجى المحاولة مرة أخرى.',
        details: error?.message,
        action: 'openContractTypeModal'
      });
      setIsLoadingContractAction(false);
    }
  };

  const actionBoxesData = [
  {
    id: 1,
    icon: 'FileText',
    title: 'عقود جديدة',
    value: '٨',
    color: '#3B82F6',
    onClick: handleMakeContractClick
  },
  {
    id: 2,
    icon: 'CheckCircle',
    title: 'تمت الموافقة',
    value: '٤',
    color: '#10B981',
    onClick: () => setShowApprovedModal(true)
  },
  {
    id: 3,
    icon: 'Download',
    title: 'تصدير تقرير',
    value: '٢',
    color: '#8B5CF6',
    onClick: () => setShowExportModal(true)
  },
  {
    id: 4,
    icon: 'Sparkles',
    title: 'توصيات ذكية',
    value: '٢',
    color: '#EC4899',
    onClick: () => setActiveTab('recommendations')
  },
  {
    id: 5,
    icon: 'Zap',
    title: 'أتمتة سير العمل',
    value: '٢',
    color: '#FCA5A5',
    onClick: () => setShowWorkflowAutomationModal(true)
  },
  {
    id: 6,
    icon: 'Activity',
    title: 'مراقبة الأتمتة',
    value: '٢',
    color: '#8B5CF6',
    onClick: () => setShowMonitoringDashboard(true)
  },
  {
    id: 7,
    icon: 'ListChecks',
    title: 'سجل المهام',
    value: '٠',
    color: '#06B6D4',
    borderColor: 'rgba(6, 182, 212, 0.6)',
    bgGradient: 'linear-gradient(135deg, rgba(6, 182, 212, 0.35) 0%, rgba(34, 211, 238, 0.4) 100%)',
    onClick: () => setShowWorkflowJobsModal(true)
  },
  {
    id: 8,
    icon: 'Zap',
    title: 'قواعد الأتمتة',
    value: '٥',
    color: '#F59E0B',
    borderColor: 'rgba(245, 158, 11, 0.6)',
    bgGradient: 'linear-gradient(135deg, rgba(245, 158, 11, 0.35) 0%, rgba(251, 191, 36, 0.4) 100%)',
    onClick: () => setShowAutomationRulesModal(true)
  },
  {
    id: 'archive',
    label: 'أرشيف العقود',
    icon: 'Archive',
    color: 'text-muted-foreground',
    onClick: () => setShowContractArchiveModal(true)
  },
  {
    id: 'documents',
    label: 'إدارة المستندات',
    icon: 'FolderOpen',
    color: 'text-primary',
    onClick: () => setShowDocumentManager(true)
  }];

  // Load recommendations, clients, and properties
  React.useEffect(() => {
    if (user?.id && activeTab === 'recommendations') {
      loadRecommendations();
      loadClients();
      loadProperties();
    }
  }, [user?.id, activeTab]);

  const loadRecommendations = async () => {
    try {
      const data = await getRecommendations(user?.id, filters);
      setRecommendations(data);
    } catch (error) {
      console.error('Error loading recommendations:', error);
    }
  };

  const loadClients = async () => {
    try {
      const data = await clientService?.getAll(user?.id);
      setClients(data);
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  const loadProperties = async () => {
    try {
      const data = await propertyService?.getAll(user?.id);
      setProperties(data);
    } catch (error) {
      console.error('Error loading properties:', error);
    }
  };

  const handleGenerateRecommendation = async (clientId) => {
    try {
      setIsGeneratingRecommendation(true);
      const client = clients?.find((c) => c?.id === clientId);
      if (!client) {
        alert('العميل غير موجود');
        return;
      }

      const recommendation = await generateClientRecommendations(client, properties, []);
      const saved = await saveRecommendation(recommendation, user?.id);

      await loadRecommendations();
      setSelectedRecommendationId(saved?.id);
      alert('تم إنشاء التوصية بنجاح');
    } catch (error) {
      console.error('Error generating recommendation:', error);
      alert(error?.message || 'فشل في إنشاء التوصية');
    } finally {
      setIsGeneratingRecommendation(false);
    }
  };

  const handleApproveRecommendation = async (recommendationId, sendToClient) => {
    try {
      await approveRecommendation(recommendationId, user?.id, sendToClient);
      await loadRecommendations();
      alert(sendToClient ? 'تمت الموافقة وإرسال التوصية للعميل' : 'تمت الموافقة على التوصية');
    } catch (error) {
      console.error('Error approving recommendation:', error);
      alert(error?.message || 'فشل في الموافقة على التوصية');
    }
  };

  const handleRejectRecommendation = async (recommendationId, reason) => {
    try {
      await rejectRecommendation(recommendationId, user?.id, reason);
      await loadRecommendations();
      alert('تم رفض التوصية');
    } catch (error) {
      console.error('Error rejecting recommendation:', error);
      alert(error?.message || 'فشل في رفض التوصية');
    }
  };

  const handleSendRecommendationToClient = async (recommendationId) => {
    try {
      await sendRecommendationToClient(recommendationId);
      await loadRecommendations();
      alert('تم إرسال التوصية للعميل بنجاح');
    } catch (error) {
      console.error('Error sending recommendation:', error);
      alert(error?.message || 'فشل في إرسال التوصية');
    }
  };

  const handleSendToClient = async (recommendationId) => {
    try {
      await sendRecommendationToClient(recommendationId);
      await loadRecommendations();
      alert('تم إرسال التوصية للعميل بنجاح');
    } catch (error) {
      console.error('Error sending recommendation:', error);
      alert(error?.message || 'فشل في إرسال التوصية');
    }
  };

  const handleContractTypeSelect = async (typeConfig) => {
    console.log('[DEBUG] handleContractTypeSelect called with:', typeConfig);
    setIsLoadingContractAction(true);
    setContractActionError(null);
    setLastAttemptedAction({ action: 'selectContractType', data: typeConfig });

    try {
      console.log('[INFO] Contract type selected:', {
        type: typeConfig?.type?.id,
        label: typeConfig?.type?.label,
        timestamp: new Date()?.toISOString()
      });

      setShowContractTypeModal(false);
      setSelectedContractTypeConfig(typeConfig);
      
      console.log('[DEBUG] Opening contract source selection modal');
      setShowContractSourceModal(true);

      // Trigger automation rules
      try {
        console.log('[INFO] Triggering automation rules for contract_created event');
        const contractData = {
          id: 'temp-' + Date.now(),
          contract_type: typeConfig?.type?.id,
          contract_value: 0,
          property_type: '',
          client_segment: ''
        };
        
        await automationRulesService?.triggerRules('contract_created', contractData);
        console.log('[SUCCESS] Automation rules triggered successfully');
      } catch (error) {
        console.error('[ERROR] Failed to trigger automation rules:', {
          error: error?.message,
          stack: error?.stack,
          contractType: typeConfig?.type?.id
        });
        // Don't block the flow if automation fails
      }

      setIsLoadingContractAction(false);
      console.log('[SUCCESS] Contract type selection completed successfully');
    } catch (error) {
      console.error('[ERROR] handleContractTypeSelect failed:', {
        error: error?.message,
        stack: error?.stack,
        typeConfig,
        timestamp: new Date()?.toISOString()
      });
      setContractActionError({
        message: 'فشل في اختيار نوع العقد. يرجى المحاولة مرة أخرى.',
        details: error?.message,
        action: 'selectContractType'
      });
      setIsLoadingContractAction(false);
    }
  };

  const handleContractSourceSelect = (sourceId) => {
    console.log('[DEBUG] handleContractSourceSelect called with:', sourceId);
    setIsLoadingContractAction(true);
    setContractActionError(null);
    setLastAttemptedAction({ action: 'selectContractSource', data: sourceId });

    try {
      console.log('[INFO] Contract source selected:', {
        sourceId,
        contractType: selectedContractTypeConfig?.type?.id,
        timestamp: new Date()?.toISOString()
      });

      setShowContractSourceModal(false);

      if (sourceId === 'new') {
        console.log('[DEBUG] Opening new contract form');
        // Open appropriate contract form based on type
        switch (selectedContractTypeConfig?.type?.id) {
          case 'marketing': console.log('[INFO] Opening marketing contract modal');
            setShowMarketingContractModal(true);
            break;
          case 'client_representation': console.log('[INFO] Opening buyer representation modal');
            setShowBuyerRepresentationModal(true);
            break;
          case 'rent': case'buying': case'selling': case'property_management': console.log('[INFO] Opening general contract modal');
            setShowNewContractModal(true);
            break;
          default:
            console.warn('[WARN] Unknown contract type:', selectedContractTypeConfig?.type?.id);
            throw new Error('نوع العقد غير معروف');
        }
      } else if (sourceId === 'archive') {
        console.log('[DEBUG] Opening contract archive modal');
        setShowContractArchiveModal(true);
      } else {
        console.warn('[WARN] Unknown source ID:', sourceId);
        throw new Error('مصدر العقد غير معروف');
      }

      setIsLoadingContractAction(false);
      console.log('[SUCCESS] Contract source selection completed successfully');
    } catch (error) {
      console.error('[ERROR] handleContractSourceSelect failed:', {
        error: error?.message,
        stack: error?.stack,
        sourceId,
        contractType: selectedContractTypeConfig?.type?.id,
        timestamp: new Date()?.toISOString()
      });
      setContractActionError({
        message: 'فشل في اختيار مصدر العقد. يرجى المحاولة مرة أخرى.',
        details: error?.message,
        action: 'selectContractSource'
      });
      setIsLoadingContractAction(false);
    }
  };

  const handleArchiveContractSelect = (contract) => {
    console.log('[DEBUG] handleArchiveContractSelect called with:', contract?.id);
    setIsLoadingContractAction(true);
    setContractActionError(null);
    setLastAttemptedAction({ action: 'selectArchiveContract', data: contract });

    try {
      console.log('[INFO] Archive contract selected:', {
        contractId: contract?.id,
        contractType: selectedContractTypeConfig?.type?.id,
        timestamp: new Date()?.toISOString()
      });

      setShowContractArchiveModal(false);

      // Pre-fill the contract form with archived contract data
      console.log('[DEBUG] Pre-filling contract form with archived data');

      // Open appropriate contract form with pre-filled data
      switch (selectedContractTypeConfig?.type?.id) {
        case 'marketing': console.log('[INFO] Opening marketing contract with archived data');
          setSelectedContractForMarketing(contract);
          setShowMarketingContractModal(true);
          break;
        case 'client_representation': console.log('[INFO] Opening buyer representation with archived data');
          setSelectedContractForBuyer(contract);
          setShowBuyerRepresentationModal(true);
          break;
        case 'rent': case'buying': case'selling': case'property_management': console.log('[INFO] Opening general contract with archived data');
          setShowNewContractModal(true);
          break;
        default:
          console.warn('[WARN] Unknown contract type for archive:', selectedContractTypeConfig?.type?.id);
          throw new Error('نوع العقد غير معروف');
      }

      setIsLoadingContractAction(false);
      console.log('[SUCCESS] Archive contract selection completed successfully');
    } catch (error) {
      console.error('[ERROR] handleArchiveContractSelect failed:', {
        error: error?.message,
        stack: error?.stack,
        contractId: contract?.id,
        contractType: selectedContractTypeConfig?.type?.id,
        timestamp: new Date()?.toISOString()
      });
      setContractActionError({
        message: 'فشل في تحميل العقد من الأرشيف. يرجى المحاولة مرة أخرى.',
        details: error?.message,
        action: 'selectArchiveContract'
      });
      setIsLoadingContractAction(false);
    }
  };

  // Retry function for failed actions
  const handleRetryAction = () => {
    console.log('[INFO] Retrying last action:', lastAttemptedAction);
    setContractActionError(null);

    if (!lastAttemptedAction) {
      console.warn('[WARN] No action to retry');
      return;
    }

    try {
      switch (lastAttemptedAction?.action) {
        case 'selectContractType':
          handleContractTypeSelect(lastAttemptedAction?.data);
          break;
        case 'selectContractSource':
          handleContractSourceSelect(lastAttemptedAction?.data);
          break;
        case 'selectArchiveContract':
          handleArchiveContractSelect(lastAttemptedAction?.data);
          break;
        default:
          console.warn('[WARN] Unknown action to retry:', lastAttemptedAction?.action);
      }
    } catch (error) {
      console.error('[ERROR] Retry failed:', error);
      setContractActionError({
        message: 'فشلت إعادة المحاولة. يرجى تحديث الصفحة والمحاولة مرة أخرى.',
        details: error?.message,
        action: lastAttemptedAction?.action
      });
    }
  };

  const ExportModal = ({ isOpen, onClose }) => {
    const [exportFormat, setExportFormat] = useState('pdf');
    const [exportRange, setExportRange] = useState('all');

    if (!isOpen) return null;

    const handleExport = () => {
      console.log('Exporting report:', { format: exportFormat, range: exportRange });
      alert(`جاري تصدير التقرير بصيغة ${exportFormat === 'pdf' ? 'PDF' : exportFormat === 'excel' ? 'Excel' : 'CSV'}`);
      onClose();
    };

    return (
      <>
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[10002]" onClick={onClose} />
        <div
          className="fixed z-[10003] bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl shadow-2xl border-2 w-full max-w-md mx-4"
          style={{
            borderColor: '#8B5CF6',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)'
          }}>

          <div className="p-6" dir="rtl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-bold text-white">تصدير تقرير العقود</h3>
              <button onClick={onClose} className="p-2 hover:bg-gray-700 rounded-lg transition-colors">
                <Icon name="X" size={20} color="#9CA3AF" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-white font-semibold mb-2 block text-right">صيغة التصدير</label>
                <select
                  value={exportFormat}
                  onChange={(e) => setExportFormat(e?.target?.value)}
                  className="w-full bg-gray-800/50 border border-gray-700 rounded-lg p-3 text-white text-right"
                  dir="rtl">

                  <option value="pdf">PDF</option>
                  <option value="excel">Excel</option>
                  <option value="csv">CSV</option>
                </select>
              </div>

              <div>
                <label className="text-white font-semibold mb-2 block text-right">نطاق التصدير</label>
                <select
                  value={exportRange}
                  onChange={(e) => setExportRange(e?.target?.value)}
                  className="w-full bg-gray-800/50 border border-gray-700 rounded-lg p-3 text-white text-right"
                  dir="rtl">

                  <option value="all">جميع العقود</option>
                  <option value="pending">العقود قيد المراجعة</option>
                  <option value="approved">العقود المعتمدة</option>
                  <option value="rejected">العقود المرفوضة</option>
                </select>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleExport}
                  className="flex-1 py-2.5 rounded-lg font-semibold text-white bg-green-600 hover:bg-green-700 transition-colors flex items-center justify-center gap-2">

                  <Icon name="Download" size={18} color="#FFF" />
                  تصدير
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 py-2.5 rounded-lg font-semibold text-white bg-gray-700 hover:bg-gray-600 transition-colors">

                  إلغاء
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  const ApprovedContractsModal = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    const approvedContracts = contracts?.filter((c) => c?.status === 'approved');

    return (
      <>
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[10002]" onClick={onClose} />
        <div
          className="fixed z-[10003] bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl shadow-2xl border-2 w-full max-w-2xl mx-4 max-h-[85vh] overflow-y-auto"
          style={{
            borderColor: '#10B981',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)'
          }}>

          <div className="p-6" dir="rtl">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-600/20 rounded-full">
                  <Icon name="CheckCircle" size={24} color="#10B981" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">العقود المعتمدة</h3>
                  <p className="text-sm text-gray-400">إجمالي {approvedContracts?.length} عقد</p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-gray-700 rounded-lg transition-colors">
                <Icon name="X" size={20} color="#9CA3AF" />
              </button>
            </div>

            <div className="space-y-3">
              {approvedContracts?.length === 0 ?
              <div className="text-center py-8">
                  <Icon name="FileX" size={48} color="#6B7280" />
                  <p className="text-gray-400 mt-4">لا توجد عقود معتمدة</p>
                </div> :

              approvedContracts?.map((contract) =>
              <div
                key={contract?.id}
                className="bg-gray-800/50 rounded-lg p-4 border border-gray-700 hover:border-green-500/50 transition-all cursor-pointer"
                onClick={() => {
                  setSelectedContractId(contract?.id);
                  onClose();
                }}>

                    <div className="flex items-start gap-4">
                      <img
                    src={contract?.propertyImage}
                    alt={contract?.propertyImageAlt}
                    className="w-20 h-20 rounded-lg object-cover" />

                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="text-white font-semibold">{contract?.propertyTitle}</h4>
                            <p className="text-sm text-gray-400">{contract?.contractNumber}</p>
                          </div>
                          <span className="px-3 py-1 bg-green-600/20 text-green-400 rounded-full text-xs font-semibold">
                            {contract?.statusLabel}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div className="flex items-center gap-2 text-gray-400">
                            <Icon name="User" size={14} color="#9CA3AF" />
                            <span>{contract?.clientName}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-400">
                            <Icon name="Calendar" size={14} color="#9CA3AF" />
                            <span>{contract?.submittedDate}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
              )
              }
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowExportModal(true);
                  onClose();
                }}
                className="flex-1 py-2.5 rounded-lg font-semibold text-white bg-green-600 hover:bg-green-700 transition-colors flex items-center justify-center gap-2">

                <Icon name="Download" size={18} color="#FFF" />
                تصدير القائمة
              </button>
              <button
                onClick={onClose}
                className="flex-1 py-2.5 rounded-lg font-semibold text-white bg-gray-700 hover:bg-gray-600 transition-colors">

                إغلاق
              </button>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <Layout userRole="admin" userName="أحمد محمد" onQuickMenuToggle={handleQuickMenuToggle}>
      <SubHeader pageTitle="العقود والموافقات" />
      <ActionBoxes actions={actionBoxesData} showNewContractModal={showNewContractModal} setShowNewContractModal={setShowNewContractModal} contractTypeConfig={selectedContractTypeConfig} />

      {/* Loading Overlay */}
      {isLoadingContractAction && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10004] flex items-center justify-center">
          <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl shadow-2xl border-2 border-blue-500 p-8 max-w-md mx-4">
            <div className="flex flex-col items-center gap-4">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <Icon name="FileText" size={24} color="#3B82F6" />
                </div>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold text-white mb-2">جاري المعالجة...</h3>
                <p className="text-gray-400 text-sm">يرجى الانتظار بينما نقوم بمعالجة طلبك</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {contractActionError && (
        <>
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[10005]" onClick={() => setContractActionError(null)} />
          <div className="fixed z-[10006] bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl shadow-2xl border-2 w-full max-w-md mx-4"
            style={{
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)'
            }}>
            <div className="p-6" dir="rtl">
              <div className="flex items-start gap-4 mb-6">
                <div className="p-3 bg-red-500/20 rounded-full">
                  <Icon name="AlertTriangle" size={24} color="#EF4444" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white mb-2">حدث خطأ</h3>
                  <p className="text-gray-300 text-sm mb-2">{contractActionError?.message}</p>
                  {contractActionError?.details && (
                    <div className="bg-gray-800/50 rounded-lg p-3 mt-3">
                      <p className="text-xs text-gray-400 font-mono">
                        <strong>التفاصيل التقنية:</strong> {contractActionError?.details}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleRetryAction}
                  className="flex-1 py-3 rounded-lg font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors flex items-center justify-center gap-2">
                  <Icon name="RotateCw" size={18} color="#FFF" />
                  إعادة المحاولة
                </button>
                <button
                  onClick={() => {
                    console.log('[INFO] Error dismissed by user');
                    setContractActionError(null);
                    setLastAttemptedAction(null);
                  }}
                  className="flex-1 py-3 rounded-lg font-semibold text-white bg-gray-700 hover:bg-gray-600 transition-colors flex items-center justify-center gap-2">
                  <Icon name="X" size={18} color="#FFF" />
                  إغلاق
                </button>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-700">
                <p className="text-xs text-gray-500 text-center">
                  إذا استمرت المشكلة، يرجى الاتصال بالدعم الفني
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="min-h-screen bg-background p-4 md:p-6 lg:p-8">
        <div className="space-y-4 md:space-y-6">
          
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
                سير عمل الموافقة على العقود
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                إدارة ومراجعة العقود من خلال نظام الموافقة متعدد المستويات
              </p>
            </div>
            <Button
              variant="default"
              size="sm"
              iconName="Plus"
              iconPosition="right"
              onClick={() => setShowNewContractModal(true)}>

              عقد جديد
            </Button>
          </div>

          <div className="flex-1 overflow-hidden">
            <div className="h-full flex flex-col">
              {/* Tabs */}
              <div className="bg-card border-b border-border px-4 md:px-6">
                <div className="flex gap-4">
                  <button
                    onClick={() => setActiveTab('contracts')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'contracts' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`
                    }
                    aria-label="عرض العقود">

                    <div className="flex items-center gap-2">
                      <Icon name="FileText" size={18} />
                      <span>العقود</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab('recommendations')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'recommendations' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`
                    }
                    aria-label="عرض التوصيات الذكية">

                    <div className="flex items-center gap-2">
                      <Icon name="Sparkles" size={18} />
                      <span>التوصيات الذكية</span>
                      {recommendations?.filter((r) => r?.approvalStatus === 'pending')?.length > 0 &&
                      <span className="bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full">
                          {recommendations?.filter((r) => r?.approvalStatus === 'pending')?.length}
                        </span>
                      }
                    </div>
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-hidden">
                {activeTab === 'contracts' ?
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 h-full">
                    <div className="lg:col-span-2 border-r border-border overflow-y-auto">
                      <div className="p-4 md:p-6 border-b border-border sticky top-0 bg-card z-10">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-base md:text-lg font-heading font-semibold text-foreground">
                            قائمة الموافقات
                          </h3>
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-1 rounded-full bg-primary text-primary-foreground text-xs font-medium font-data">
                              {filteredContracts?.length}
                            </span>
                          </div>
                        </div>

                        <WorkflowStats stats={stats} />

                        <FilterBar filters={filters} onFilterChange={handleFilterChange} />
                      </div>

                      <div className="p-4 md:p-6 space-y-4">
                        {isLoadingContracts ? (
                          <div className="flex flex-col items-center justify-center py-12">
                            <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin mb-4"></div>
                            <p className="text-sm text-muted-foreground">جاري تحميل العقود...</p>
                          </div>
                        ) : contractsError ? (
                          <div className="text-center py-12">
                            <Icon name="AlertTriangle" size={48} color="var(--color-error)" className="mx-auto mb-4" />
                            <p className="text-sm text-error mb-4">{contractsError}</p>
                            <Button onClick={loadContracts} variant="outline" size="sm">
                              <Icon name="RotateCw" size={16} />
                              <span>إعادة المحاولة</span>
                            </Button>
                          </div>
                        ) : filteredContracts?.length === 0 ? (
                          <div className="text-center py-8">
                            <Icon name="FileX" size={48} color="var(--color-muted-foreground)" />
                            <p className="text-sm text-muted-foreground mt-4">
                              لا توجد عقود تطابق معايير البحث
                            </p>
                          </div>
                        ) : (
                          filteredContracts?.map((contract) => (
                            <ContractCard
                              key={contract?.id}
                              contract={{
                                id: contract?.id,
                                contractNumber: contract?.contract_number,
                                propertyTitle: contract?.property?.title || 'غير محدد',
                                propertyImage: contract?.property?.images?.[0] || '/assets/images/no_image.png',
                                propertyImageAlt: `صورة ${contract?.property?.title || 'العقار'}`,
                                propertyType: contract?.property?.property_type,
                                clientName: contract?.client?.name || 'غير محدد',
                                status: contract?.status,
                                statusLabel: contract?.status === 'pending' ? 'قيد المراجعة' : 
                                             contract?.status === 'approved' ? 'موافق عليه' :
                                             contract?.status === 'rejected' ? 'مرفوض' :
                                             contract?.status === 'active' ? 'نشط' : 'منتهي',
                                priority: 'normal',
                                priorityLabel: 'عادي',
                                submittedDate: new Date(contract?.created_at)?.toLocaleDateString('ar-EG'),
                                daysWaiting: Math.floor((new Date() - new Date(contract?.created_at)) / (1000 * 60 * 60 * 24)),
                                currentStage: 'legal_review'
                              }}
                              isSelected={selectedContractId === contract?.id}
                              onClick={() => setSelectedContractId(contract?.id)}
                              userRole={userRole}
                            />
                          ))
                        )}
                      </div>
                    </div>

                    <div className="lg:col-span-1 border-r border-border overflow-y-auto bg-muted/30">
                      {selectedContractId &&
                    <div className="p-4 md:p-6">
                          <MarketingContractDetails contractId={selectedContractId} />
                        </div>
                    }
                    </div>

                    <div className="lg:col-span-1 overflow-y-auto">
                      <ApprovalPanel
                      contract={selectedContract}
                      userRole={userRole}
                      onApprove={handleApprove}
                      onReject={handleReject}
                      onRequestChanges={handleRequestChanges} />
                    </div>
                  </div> :

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 h-full">
                    {/* Recommendations List */}
                    <div className="lg:col-span-1 border-r border-border overflow-y-auto">
                      <div className="p-4 md:p-6 border-b border-border sticky top-0 bg-card z-10">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-base md:text-lg font-heading font-bold text-foreground">
                            التوصيات الذكية ({filteredRecommendations?.length})
                          </h3>
                          <Button
                          onClick={() => {
                            if (clients?.length === 0) {
                              alert('لا يوجد عملاء. يرجى إضافة عملاء أولاً.');
                              return;
                            }
                            const clientId = clients?.[0]?.id;
                            handleGenerateRecommendation(clientId);
                          }}
                          disabled={isGeneratingRecommendation || clients?.length === 0}
                          className="bg-primary hover:bg-primary/90 text-white text-xs md:text-sm"
                          aria-label="إنشاء توصية جديدة">

                            <Icon name="Sparkles" size={16} />
                            <span>{isGeneratingRecommendation ? 'جاري الإنشاء...' : 'إنشاء توصية'}</span>
                          </Button>
                        </div>
                      </div>
                      <div className="p-4 md:p-6 space-y-4">
                        {filteredRecommendations?.length === 0 ?
                      <div className="text-center py-12">
                            <Icon name="Sparkles" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                            <p className="text-sm text-muted-foreground mb-4">
                              لا توجد توصيات حالياً
                            </p>
                            <Button
                          onClick={() => {
                            if (clients?.length === 0) {
                              alert('لا يوجد عملاء. يرجى إضافة عملاء أولاً.');
                              return;
                            }
                            const clientId = clients?.[0]?.id;
                            handleGenerateRecommendation(clientId);
                          }}
                          disabled={isGeneratingRecommendation || clients?.length === 0}
                          className="bg-primary hover:bg-primary/90 text-white"
                          aria-label="إنشاء أول توصية">

                              <Icon name="Plus" size={18} />
                              <span>إنشاء أول توصية</span>
                            </Button>
                          </div> :

                      filteredRecommendations?.map((recommendation) =>
                      <RecommendationCard
                        key={recommendation?.id}
                        recommendation={recommendation}
                        isSelected={selectedRecommendationId === recommendation?.id}
                        onClick={() => setSelectedRecommendationId(recommendation?.id)}
                        userRole={userRole} />

                      )
                      }
                      </div>
                    </div>

                    {/* Recommendation Details */}
                    <div className="lg:col-span-1 border-r border-border overflow-y-auto bg-muted/30">
                      <RecommendationDetails recommendation={selectedRecommendation} />
                    </div>

                    {/* Approval Panel */}
                    <div className="lg:col-span-1 overflow-y-auto">
                      <RecommendationApprovalPanel
                      recommendation={selectedRecommendation}
                      userRole={userRole}
                      onApprove={handleApproveRecommendation}
                      onReject={handleRejectRecommendation}
                      onSendToClient={handleSendToClient} />

                    </div>
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Contract Type Selection Modal */}
      <ContractTypeSelectionModal
        isOpen={showContractTypeModal}
        onClose={() => setShowContractTypeModal(false)}
        onSelectType={handleContractTypeSelect} />


      {/* Contract Source Selection Modal */}
      <ContractSourceSelectionModal
        isOpen={showContractSourceModal}
        onClose={() => {
          setShowContractSourceModal(false);
          setSelectedContractTypeConfig(null);
        }}
        contractTypeConfig={selectedContractTypeConfig}
        onSelectSource={handleContractSourceSelect} />


      {/* Contract Archive Modal */}
      <ContractArchiveModal
        isOpen={showContractArchiveModal}
        onClose={() => {
          setShowContractArchiveModal(false);
        }}
        contractTypeConfig={selectedContractTypeConfig}
        onSelectContract={handleArchiveContractSelect} />


      {/* Marketing Contract Modal */}
      {showMarketingContractModal && selectedContractForMarketing && (
        <MarketingContractModal
          isOpen={showMarketingContractModal}
          onClose={() => {
            setShowMarketingContractModal(false);
            setSelectedContractForMarketing(null);
          }}
          contractId={selectedContractForMarketing?.id}
          contractAmount={selectedContractForMarketing?.amount}
          onSuccess={() => {
            console.log('Marketing contract created successfully');
          }} />

      )}

      {showBuyerRepresentationModal && selectedContractForBuyer && (
        <BuyerRepresentationContractModal
          isOpen={showBuyerRepresentationModal}
          onClose={() => {
            setShowBuyerRepresentationModal(false);
            setSelectedContractForBuyer(null);
          }}
          contractId={selectedContractForBuyer?.id}
          contractAmount={selectedContractForBuyer?.amount}
          onSuccess={() => {
            console.log('Buyer representation contract created successfully');
          }} />

      )}

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)} />


      <ApprovedContractsModal
        isOpen={showApprovedModal}
        onClose={() => setShowApprovedModal(false)} />


      {/* Workflow Automation Modal */}
      <WorkflowAutomationModal
        isOpen={showWorkflowAutomationModal}
        onClose={() => setShowWorkflowAutomationModal(false)}
        contracts={filteredContracts} />


      {/* Workflow Jobs Modal */}
      <WorkflowJobsModal
        isOpen={showWorkflowJobsModal}
        onClose={() => setShowWorkflowJobsModal(false)} />


      <WorkflowMonitoringDashboard
        isOpen={showMonitoringDashboard}
        onClose={() => setShowMonitoringDashboard(false)} />

      {/* Automation Rules Modal */}
      <AutomationRulesModal
        isOpen={showAutomationRulesModal}
        onClose={() => setShowAutomationRulesModal(false)}
      />

      {/* Real-time notification toast */}
      {showRealtimeToast && realtimeNotification && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 animate-slide-down">
          <div className="bg-white rounded-lg shadow-2xl border-r-4 border-blue-500 p-4 min-w-[300px] max-w-md">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Icon name="Bell" className="w-5 h-5 text-blue-600" />
                </div>
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-gray-900 mb-1">
                  إشعار فوري
                </h4>
                <p className="text-sm text-gray-700">
                  {realtimeNotification?.message}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {new Date()?.toLocaleTimeString('ar-JO', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
              <button
                onClick={() => setShowRealtimeToast(false)}
                className="flex-shrink-0 text-gray-400 hover:text-gray-600"
              >
                <Icon name="X" className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
      </Layout>
    </>
  );
};

export default ContractApprovalWorkflow;